import { useState } from "react"
import { loadStripe } from "@stripe/stripe-js"
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js"
import bbApi from "../../../api"

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
)

interface AddPaymentFormProps {
  orgId: string
  onSuccess: () => void
  onCancel: () => void
}

const AddPaymentFormInner = ({
  orgId,
  onSuccess,
  onCancel
}: AddPaymentFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [setAsDefault, setSetAsDefault] = useState(true)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const cardElement = elements.getElement(CardElement)

      if (!cardElement) {
        throw new Error("Card element not found")
      }

      // Create payment method with Stripe
      const { error: stripeError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement
        })

      if (stripeError) {
        throw new Error(stripeError.message)
      }

      if (!paymentMethod) {
        throw new Error("Failed to create payment method")
      }

      // Send to backend
      await bbApi.payment.addPaymentMethod({
        org_id: orgId,
        payment_method_id: paymentMethod.id,
        set_as_default: setAsDefault
      })

      onSuccess()
    } catch (err: any) {
      setError(err.message || "Failed to add payment method")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div className="border rounded-md p-3">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4"
                  }
                },
                invalid: {
                  color: "#9e2146"
                }
              }
            }}
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="setAsDefault"
          checked={setAsDefault}
          onChange={e => setSetAsDefault(e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
        />
        <label htmlFor="setAsDefault" className="ml-2 text-sm text-gray-700">
          Set as default payment method
        </label>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
          {error}
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isLoading}
          className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? "Adding..." : "Add Card"}
        </button>
      </div>
    </form>
  )
}

const AddPaymentForm = (props: AddPaymentFormProps) => {
  return (
    <Elements stripe={stripePromise}>
      <AddPaymentFormInner {...props} />
    </Elements>
  )
}

export default AddPaymentForm
